import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Upload,
  FileText,
  Loader2,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Plus,
  X,
  Eye,
  EyeOff,
  BookOpen,
  ChevronLeft,
  Send,
} from "lucide-react";
import API from "../lib/api";
import AdminGuard from "../components/admin/AdminGuard";
import AdminShell from "../components/admin/AdminShell";
import { errorMessage } from "../lib/errors";

/**
 * Phase 7.3 — Admin Content Import Page
 * --------------------------------------------------------------
 * Workflow:
 *   1) Admin uploads a PDF / DOCX / MD / TXT file.
 *   2) Backend extracts the text + uses AI (Emergent LLM gpt-4o-mini) to suggest
 *      topic, tags, summary, content_type with confidence.
 *   3) Admin reviews & edits the AI suggestions (topic dropdown, tag chips, summary).
 *   4) Admin can either:
 *      a) Open the new draft in the existing Content Editor (full markdown UX), OR
 *      b) Queue the draft for the Phase 7.4 Ask Donny KB ingestion (sets meta.import.kb_queued).
 *
 * Editorial control is human-first — AI is a head-start, not a decision-maker.
 */
export default function AdminContentImportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [meta, setMeta] = useState({ topics: [], content_types: [], allowed_file_extensions: [], max_upload_bytes: 0 });
  const [metaLoading, setMetaLoading] = useState(true);

  const [file, setFile] = useState(null);
  const [locale, setLocale] = useState("en");
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(true);

  // Draft state once the upload completes
  const [draft, setDraft] = useState(null);
  const [editedSuggestions, setEditedSuggestions] = useState(null);
  const [savingMeta, setSavingMeta] = useState(false);
  const [queuingKB, setQueuingKB] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const fileInputRef = useRef(null);

  // Load taxonomy & limits on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setMetaLoading(true);
      try {
        const { data } = await API.get("/admin/import/topics");
        if (!cancelled) setMeta(data || {});
      } catch (err) {
        if (!cancelled) toast.error(errorMessage(err, t("admin.import.meta_load_failed", "Couldn\u2019t load import taxonomy")));
      } finally {
        if (!cancelled) setMetaLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  const allowedExtsLabel = (meta.allowed_file_extensions || []).map((e) => "." + e).join(", ");
  const maxMB = Math.round(((meta.max_upload_bytes || 0) / (1024 * 1024))) || 20;

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if ((meta.allowed_file_extensions || []).length && !meta.allowed_file_extensions.includes(ext)) {
      toast.error(t("admin.import.unsupported_ext", "Unsupported file type: .{{ext}}", { ext }));
      e.target.value = "";
      return;
    }
    if (meta.max_upload_bytes && f.size > meta.max_upload_bytes) {
      toast.error(t("admin.import.too_large", "File too large (max {{mb}}MB)", { mb: maxMB }));
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const resetState = () => {
    setFile(null);
    setDraft(null);
    setEditedSuggestions(null);
    setTagInput("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error(t("admin.import.choose_file", "Please choose a file to import"));
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("locale", locale);
      const { data } = await API.post("/admin/import/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const item = data.item || {};
      setDraft(item);
      const importMeta = (item.meta || {}).import || {};
      const suggestions = importMeta.suggestions || {};
      setEditedSuggestions({
        topic: item.topic || suggestions.topic || "",
        tags: item.tags || suggestions.tags || [],
        summary: (item.summary_by_locale && (item.summary_by_locale.en || item.summary_by_locale[locale])) || suggestions.summary || "",
        content_type: item.type || suggestions.content_type || "article",
      });
      toast.success(t("admin.import.upload_success", "Imported \u201C{{title}}\u201D as draft", {
        title: (item.title_by_locale && (item.title_by_locale.en || Object.values(item.title_by_locale)[0])) || file.name,
      }));
    } catch (err) {
      toast.error(errorMessage(err, t("admin.import.upload_failed", "Import failed")));
    } finally {
      setUploading(false);
    }
  };

  const addTag = (raw) => {
    const cleaned = (raw || "").toString().trim().toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
    if (!cleaned) return;
    setEditedSuggestions((s) => ({
      ...s,
      tags: s && s.tags && s.tags.includes(cleaned) ? s.tags : [...((s && s.tags) || []), cleaned],
    }));
  };

  const removeTag = (tag) => {
    setEditedSuggestions((s) => ({ ...s, tags: (s.tags || []).filter((x) => x !== tag) }));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(tagInput);
      setTagInput("");
    }
  };

  const persistSuggestions = async (extraPatch = {}) => {
    if (!draft) return null;
    setSavingMeta(true);
    try {
      const patch = {
        topic: editedSuggestions.topic || null,
        tags: editedSuggestions.tags || [],
        type: editedSuggestions.content_type || "article",
        summary_by_locale: {
          ...(draft.summary_by_locale || {}),
          en: editedSuggestions.summary || (draft.summary_by_locale && draft.summary_by_locale.en) || "",
        },
        ...extraPatch,
      };
      const { data } = await API.put(`/admin/content/${draft.content_id}`, patch);
      setDraft(data.item);
      return data.item;
    } catch (err) {
      toast.error(errorMessage(err, t("admin.import.save_failed", "Couldn\u2019t save edits")));
      return null;
    } finally {
      setSavingMeta(false);
    }
  };

  const handleOpenInEditor = async () => {
    const saved = await persistSuggestions();
    if (saved) navigate(`/admin/content/${saved.content_id}/edit`);
  };

  const handleQueueKB = async () => {
    const saved = await persistSuggestions();
    if (!saved) return;
    setQueuingKB(true);
    try {
      const { data } = await API.post(`/admin/import/${saved.content_id}/queue-kb`);
      setDraft(data.item);
      toast.success(t("admin.import.kb_queued_success", "Draft sent to Ask Donny KB ingestion queue."));
    } catch (err) {
      toast.error(errorMessage(err, t("admin.import.kb_queued_failed", "Couldn\u2019t queue for KB")));
    } finally {
      setQueuingKB(false);
    }
  };

  const handleImportAnother = () => {
    resetState();
  };

  const draftBodyPreview = draft && draft.body_md_by_locale
    ? (draft.body_md_by_locale.en || draft.body_md_by_locale[locale] || Object.values(draft.body_md_by_locale)[0] || "")
    : "";

  const draftImportMeta = (draft && (draft.meta || {}).import) || {};
  const aiSuggestions = draftImportMeta.suggestions || {};
  const aiSource = aiSuggestions.source || "fallback";
  const confidence = typeof aiSuggestions.confidence === "number" ? aiSuggestions.confidence : 0;
  const kbQueued = !!draftImportMeta.kb_queued;

  return (
    <AdminGuard>
      <AdminShell active="content">
        <div className="space-y-6" data-testid="admin-content-import">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="eyebrow mb-2">{t("admin.import.eyebrow", "\u2014 Content Import")}</div>
              <h2 className="font-display text-2xl md:text-3xl leading-tight" data-testid="admin-import-title">
                {t("admin.import.title", "Import documents into the ecosystem")}
              </h2>
              <p className="text-sm text-white/65 mt-2 max-w-2xl leading-relaxed">
                {t(
                  "admin.import.subtitle",
                  "Upload PDFs, Word documents, Markdown, or plain text. We\u2019ll extract the body, ask the AI for topic, tags, and a short summary \u2014 and create an editable draft you can publish or send to Ask Donny\u2019s knowledge base."
                )}
              </p>
            </div>
            <button
              onClick={() => navigate("/admin/content")}
              className="btn-ghost text-sm inline-flex items-center gap-2 self-start"
              data-testid="back-to-content-list-btn"
            >
              <ChevronLeft className="w-4 h-4" />
              {t("admin.import.back_to_list", "Back to Content Manager")}
            </button>
          </div>

          {/* Upload card */}
          {!draft && (
            <div className="card-surface p-5 md:p-6" data-testid="import-upload-card">
              <div className="flex items-center gap-2 mb-3">
                <Upload className="w-4 h-4 gold-text" />
                <h3 className="font-display text-lg">{t("admin.import.upload_title", "Upload a document")}</h3>
              </div>
              <p className="text-xs text-white/55 mb-4">
                {t("admin.import.upload_hint", "Allowed: {{exts}}. Maximum size: {{mb}}MB.", {
                  exts: allowedExtsLabel || ".pdf, .docx, .md, .txt",
                  mb: maxMB,
                })}
              </p>
              <div className="grid md:grid-cols-[1fr_180px] gap-3 items-end">
                <label
                  className="cursor-pointer block border border-dashed border-white/15 rounded-xl p-5 text-center hover:border-[#C8A24A]/40 hover:bg-white/[0.02] transition-colors"
                  data-testid="import-file-dropzone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={(meta.allowed_file_extensions || []).map((e) => "." + e).join(",")}
                    className="hidden"
                    onChange={handleFileChange}
                    data-testid="import-file-input"
                  />
                  <FileText className="w-7 h-7 mx-auto opacity-70 mb-2" />
                  {file ? (
                    <div className="space-y-0.5">
                      <div className="text-sm font-medium text-white truncate" data-testid="import-file-name">{file.name}</div>
                      <div className="text-[11px] text-white/50">{(file.size / 1024).toFixed(1)} KB</div>
                    </div>
                  ) : (
                    <div>
                      <div className="text-sm text-white/80">{t("admin.import.choose_file", "Choose a file to import")}</div>
                      <div className="text-[11px] text-white/45 mt-1">{t("admin.import.click_to_select", "Click anywhere in this box to select")}</div>
                    </div>
                  )}
                </label>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-white/55 mb-1.5 block">
                    {t("admin.import.locale_label", "Source language")}
                  </label>
                  <select
                    value={locale}
                    onChange={(e) => setLocale(e.target.value)}
                    className="w-full bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C8A24A]/40"
                    data-testid="import-locale-select"
                  >
                    {(meta.supported_locales || ["en", "fr", "es", "it", "de", "nl", "ar", "sw", "zh", "ko", "ru"]).map((l) => (
                      <option key={l} value={l}>{l.toUpperCase()}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 mt-5">
                {file && (
                  <button type="button" onClick={resetState} className="btn-ghost text-sm" data-testid="import-reset-btn">
                    {t("admin.import.clear", "Clear")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={!file || uploading || metaLoading}
                  className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="import-upload-submit"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {uploading
                    ? t("admin.import.uploading", "Importing\u2026")
                    : t("admin.import.start", "Import & analyze")}
                </button>
              </div>
            </div>
          )}

          {/* Result / editable suggestions */}
          {draft && (
            <div className="grid lg:grid-cols-[1fr_360px] gap-6" data-testid="import-result">
              {/* Preview column */}
              <div className="space-y-4">
                <div className="card-surface p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="eyebrow mb-1.5">{t("admin.import.imported_eyebrow", "\u2014 Draft created")}</div>
                      <h3 className="font-display text-xl md:text-2xl truncate" data-testid="import-draft-title">
                        {(draft.title_by_locale && (draft.title_by_locale.en || Object.values(draft.title_by_locale)[0])) || draft.slug}
                      </h3>
                      <div className="flex items-center gap-2 mt-1.5 text-xs text-white/60 flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {draftImportMeta.filename}
                        </span>
                        <span>·</span>
                        <span>{(draftImportMeta.char_count || 0).toLocaleString()} {t("admin.import.chars", "chars")}</span>
                        <span>·</span>
                        <span className="font-mono">{draft.slug}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => setPreviewOpen((v) => !v)}
                      className="btn-ghost text-xs inline-flex items-center gap-1.5"
                      data-testid="import-toggle-preview"
                    >
                      {previewOpen ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      {previewOpen
                        ? t("admin.import.hide_preview", "Hide preview")
                        : t("admin.import.show_preview", "Show preview")}
                    </button>
                  </div>
                  {previewOpen && (
                    <div
                      className="mt-4 max-h-[460px] overflow-auto rounded-lg border border-white/10 bg-black/30 p-4 text-sm text-white/85 whitespace-pre-wrap font-mono leading-relaxed"
                      data-testid="import-body-preview"
                    >
                      {draftBodyPreview || t("admin.import.no_body", "No body extracted.")}
                    </div>
                  )}
                </div>
              </div>

              {/* AI suggestions / editable side panel */}
              <div className="space-y-4">
                <div className="card-surface p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 gold-text" />
                      <h3 className="font-display text-base">{t("admin.import.ai_title", "AI suggestions")}</h3>
                    </div>
                    {aiSource === "ai" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-[#C8A24A]/40 bg-[#C8A24A]/10 gold-text">
                        <CheckCircle2 className="w-3 h-3" />
                        {t("admin.import.ai_ok", "AI")} · {(confidence * 100).toFixed(0)}%
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-300">
                        <AlertTriangle className="w-3 h-3" />
                        {t("admin.import.ai_fallback", "Fallback")}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-white/55 mb-4 leading-relaxed">
                    {t(
                      "admin.import.ai_help",
                      "Edit the topic, tags, and summary before publishing. AI suggestions are a starting point, not a final decision."
                    )}
                  </p>

                  {/* Topic */}
                  <label className="text-[11px] uppercase tracking-wider text-white/55 mb-1.5 block">
                    {t("admin.import.topic_label", "Ecosystem topic")}
                  </label>
                  <select
                    value={(editedSuggestions && editedSuggestions.topic) || ""}
                    onChange={(e) => setEditedSuggestions((s) => ({ ...s, topic: e.target.value }))}
                    className="w-full bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C8A24A]/40 mb-3"
                    data-testid="import-topic-select"
                  >
                    <option value="">{t("admin.import.topic_none", "\u2014 No topic")}</option>
                    {(meta.topics || []).map((tp) => (
                      <option key={tp} value={tp}>{tp}</option>
                    ))}
                  </select>

                  {/* Content type */}
                  <label className="text-[11px] uppercase tracking-wider text-white/55 mb-1.5 block">
                    {t("admin.import.type_label", "Content type")}
                  </label>
                  <select
                    value={(editedSuggestions && editedSuggestions.content_type) || "article"}
                    onChange={(e) => setEditedSuggestions((s) => ({ ...s, content_type: e.target.value }))}
                    className="w-full bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C8A24A]/40 mb-3"
                    data-testid="import-content-type-select"
                  >
                    {(meta.content_types || ["article"]).map((ct) => (
                      <option key={ct} value={ct}>{ct}</option>
                    ))}
                  </select>

                  {/* Tags */}
                  <label className="text-[11px] uppercase tracking-wider text-white/55 mb-1.5 block">
                    {t("admin.import.tags_label", "Tags")}
                  </label>
                  <div className="flex flex-wrap gap-1.5 mb-2" data-testid="import-tags-list">
                    {((editedSuggestions && editedSuggestions.tags) || []).map((tg) => (
                      <span
                        key={tg}
                        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-white/15 bg-white/5 text-white/85"
                        data-testid={`import-tag-${tg}`}
                      >
                        {tg}
                        <button
                          type="button"
                          onClick={() => removeTag(tg)}
                          className="text-white/55 hover:text-white"
                          aria-label={t("admin.import.remove_tag", "Remove tag {{tag}}", { tag: tg })}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    {!(editedSuggestions && editedSuggestions.tags && editedSuggestions.tags.length) && (
                      <span className="text-[11px] text-white/45">{t("admin.import.no_tags", "No tags yet")}</span>
                    )}
                  </div>
                  <div className="flex gap-1.5 mb-4">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleTagKeyDown}
                      placeholder={t("admin.import.tag_placeholder", "Add a tag and press Enter")}
                      className="flex-1 bg-[#0F1116] border border-white/10 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-[#C8A24A]/40"
                      data-testid="import-tag-input"
                    />
                    <button
                      type="button"
                      onClick={() => { addTag(tagInput); setTagInput(""); }}
                      className="btn-ghost text-xs inline-flex items-center gap-1"
                      data-testid="import-add-tag-btn"
                    >
                      <Plus className="w-3 h-3" />
                      {t("admin.import.add_tag", "Add")}
                    </button>
                  </div>

                  {/* Summary */}
                  <label className="text-[11px] uppercase tracking-wider text-white/55 mb-1.5 block">
                    {t("admin.import.summary_label", "Summary (English)")}
                  </label>
                  <textarea
                    value={(editedSuggestions && editedSuggestions.summary) || ""}
                    onChange={(e) => setEditedSuggestions((s) => ({ ...s, summary: e.target.value }))}
                    rows={3}
                    className="w-full bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C8A24A]/40 leading-relaxed resize-y"
                    data-testid="import-summary-textarea"
                  />
                </div>

                {/* Actions */}
                <div className="card-surface p-5 space-y-2.5">
                  <h3 className="font-display text-base mb-1">{t("admin.import.next_steps", "Next steps")}</h3>
                  <button
                    type="button"
                    onClick={handleOpenInEditor}
                    disabled={savingMeta}
                    className="btn-primary w-full text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="import-open-editor-btn"
                  >
                    {savingMeta ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                    {t("admin.import.open_editor", "Open in Content Editor")}
                  </button>
                  <button
                    type="button"
                    onClick={handleQueueKB}
                    disabled={savingMeta || queuingKB || kbQueued}
                    className="btn-secondary w-full text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="import-queue-kb-btn"
                  >
                    {queuingKB ? <Loader2 className="w-4 h-4 animate-spin" /> : kbQueued ? <CheckCircle2 className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                    {kbQueued
                      ? t("admin.import.kb_queued", "Queued for Ask Donny KB")
                      : t("admin.import.queue_kb", "Send to Ask Donny KB")}
                  </button>
                  <button
                    type="button"
                    onClick={handleImportAnother}
                    className="btn-ghost w-full text-xs inline-flex items-center justify-center gap-1.5"
                    data-testid="import-another-btn"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    {t("admin.import.import_another", "Import another document")}
                  </button>
                </div>

                {/* Helpful link */}
                <div className="card-surface p-4 text-[11px] text-white/55 leading-relaxed">
                  <div className="flex items-center gap-1.5 mb-1.5 text-white/75">
                    <BookOpen className="w-3.5 h-3.5 gold-text" />
                    <span className="font-medium">{t("admin.import.tip_title", "Tip")}</span>
                  </div>
                  {t(
                    "admin.import.tip_body",
                    "PDFs with selectable text work best. Scanned/image-only PDFs require OCR \u2014 we\u2019ll add that pipeline in a future phase."
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </AdminShell>
    </AdminGuard>
  );
}
